"use client";

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Image from "next/image";
import { motion, PanInfo } from "framer-motion";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import styles from "./heroSection.module.css";

/* ================= MAIN COMPONENT ================= */
export default function HeroSection({ banners }: { banners?: any[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [vWidth, setVWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(1); // Bắt đầu từ 1 vì index 0 là slide ảo
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  const displayImages = useMemo(() => {
    if (!banners || !banners.length) return [];

    const raw = banners
      .map((item) => {
        if (typeof item === "string") {
          return appendImageUrlFromAPI({ src: item, size: "2k" });
        }
        const src =
          item?.hinh_anh ||
          item?.picture ||
          Object.values(item).find((v) => typeof v === "string" && v);
        return src ? appendImageUrlFromAPI({ src, size: "2k" }) : null;
      })
      .filter(Boolean) as string[];

    if (raw.length === 0) return [];
    if (raw.length === 1) return raw;

    // Kỹ thuật gối đầu để chạy vô tận mượt mà
    return [raw[raw.length - 1], ...raw, raw[0]];
  }, [banners]);
  const totalDisplay = displayImages.length;
  const actualLength = totalDisplay - 2;

  // 2. Cập nhật chiều rộng container
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setVWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [displayImages.length]); // Cập nhật lại khi có ảnh mới

  // 3. Logic xử lý vòng lặp vô tận (Jump back/forward)
  const handleAnimationComplete = () => {
    setIsAnimating(false);
    if (currentIndex === 0) {
      // Nếu đang ở bản sao của ảnh cuối (vị trí 0), nhảy về ảnh cuối thật
      setIsTransitioning(false);
      setCurrentIndex(actualLength);
    } else if (currentIndex === totalDisplay - 1) {
      // Nếu đang ở bản sao của ảnh đầu (vị trí cuối), nhảy về ảnh đầu thật (vị trí 1)
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
  };

  // Bật lại hiệu ứng mượt sau khi đã nhảy vị trí ngầm
  useEffect(() => {
    if (!isTransitioning) {
      const raf = requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isTransitioning]);

  const handleNext = useCallback(() => {
    if (isAnimating || !isTransitioning || currentIndex >= totalDisplay - 1)
      return;
    setIsAnimating(true);
    setCurrentIndex((prev) => prev + 1);
  }, [isAnimating, isTransitioning, currentIndex, totalDisplay]);

  const handlePrev = useCallback(() => {
    if (isAnimating || !isTransitioning || currentIndex <= 0) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => prev - 1);
  }, [isAnimating, isTransitioning, currentIndex]);

  /** --- AUTO PLAY LOGIC --- **/
  useEffect(() => {
    if (actualLength <= 1) return;

    const interval = setInterval(() => {
      handleNext();
    }, 5000); // Chuyển slide mỗi 5 giây

    return () => clearInterval(interval);
  }, [handleNext, actualLength]);

  const onDragEnd = (e: any, info: PanInfo) => {
    const swipe = info.offset.x;
    const threshold = 50;
    if (swipe < -threshold) handleNext();
    else if (swipe > threshold) handlePrev();
  };

  if (totalDisplay === 0) return null;

  return (
    <section
      className={styles.hero_wrapper}
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <motion.div
          drag="x"
          // Cho phép kéo tự do, logic currentIndex sẽ quản lý vị trí dừng
          dragConstraints={{
            left: -(currentIndex * vWidth),
            right: -(currentIndex * vWidth),
          }}
          dragElastic={0.6}
          onDragEnd={onDragEnd}
          animate={{ x: -(currentIndex * vWidth) }}
          onAnimationStart={() => setIsAnimating(true)}
          onAnimationComplete={handleAnimationComplete}
          transition={
            isTransitioning
              ? { type: "spring", stiffness: 250, damping: 30, mass: 0.5 }
              : { duration: 0 } // Tắt transition khi nhảy vòng lặp
          }
          style={{
            display: "flex",
            width: `${totalDisplay * 100}%`,
            height: "100%",
            cursor: "grab",
          }}
          whileTap={{ cursor: "grabbing" }}
        >
          {displayImages.map((src, index) => (
            <div
              key={`${index}-${src}`}
              style={{
                width: vWidth || "100vw",
                flexShrink: 0,
                height: "100%",
                position: "relative",
              }}
            >
              <Image
                src={src}
                alt="Banner"
                fill
                priority={index === 1}
                className={styles.banner_image}
                style={{ objectFit: "cover" }}
                draggable={false}
              />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Điều hướng */}
      {actualLength > 1 && (
        <>
          <div
            style={{
              position: "absolute",
              top: "50%",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              transform: "translateY(-50%)",
              padding: "0 20px",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <button
              className={styles.nav_btn}
              style={{ pointerEvents: "auto" }}
              onClick={handlePrev}
            >
              ❮
            </button>
            <button
              className={styles.nav_btn}
              style={{ pointerEvents: "auto" }}
              onClick={handleNext}
            >
              ❯
            </button>
          </div>

          {/* Indicators */}
          {/* Indicators */}
        </>
      )}
    </section>
  );
}
